'use client';

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import { IconSvgPreview } from '@/components/painel/icon-library/IconSvgPreview';
import { painelInputClass, painelTextareaClass } from '@/components/painel/cms/painel-form';
import {
  painelButtonPrimaryClass,
  painelModalCancelButtonClass,
  painelSelectContentClass,
  painelSwitchClass,
} from '@/components/painel/painel-styles';
import {
  detectDangerousSvgHints,
  ICON_LIBRARY_CATEGORIES,
  slugifyIconName,
  type IconLibraryItem,
  type IconRenderMode,
} from '@/lib/icon-library';
import { cn } from '@/lib/utils';

export type IconEditorDraft = {
  name: string;
  slug: string;
  category: string;
  description: string;
  tags: string;
  svgContent: string;
  renderMode: IconRenderMode;
};

type IconLibraryEditorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  initial?: IconLibraryItem | null;
  saving?: boolean;
  onSave: (draft: IconEditorDraft) => Promise<void> | void;
  onUploadFile?: (file: File, draft: IconEditorDraft) => Promise<void> | void;
};

function emptyDraft(): IconEditorDraft {
  return {
    name: '',
    slug: '',
    category: 'outros',
    description: '',
    tags: '',
    svgContent: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"/></svg>',
    renderMode: 'mask',
  };
}

function fromItem(item: IconLibraryItem): IconEditorDraft {
  return {
    name: item.name,
    slug: item.slug,
    category: item.category || 'outros',
    description: item.description ?? '',
    tags: (item.tags ?? []).join(', '),
    svgContent: item.svgContent ?? '',
    renderMode: item.renderMode === 'img' ? 'img' : 'mask',
  };
}

export function IconLibraryEditorDialog({
  open,
  onOpenChange,
  mode,
  initial,
  saving,
  onSave,
  onUploadFile,
}: IconLibraryEditorDialogProps) {
  const [draft, setDraft] = useState<IconEditorDraft>(emptyDraft);
  const [slugTouched, setSlugTouched] = useState(false);
  const [previewSize, setPreviewSize] = useState(64);
  const [previewLight, setPreviewLight] = useState(false);
  const [customCategory, setCustomCategory] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      setDraft(fromItem(initial));
      setSlugTouched(true);
      setCustomCategory(!ICON_LIBRARY_CATEGORIES.some((c) => c.id === initial.category));
    } else {
      setDraft(emptyDraft());
      setSlugTouched(false);
      setCustomCategory(false);
    }
  }, [open, mode, initial]);

  const dangerHints = useMemo(
    () => detectDangerousSvgHints(draft.svgContent),
    [draft.svgContent]
  );

  const patch = (partial: Partial<IconEditorDraft>) =>
    setDraft((prev) => ({ ...prev, ...partial }));

  const handleNameChange = (name: string) => {
    patch({
      name,
      ...(slugTouched ? {} : { slug: slugifyIconName(name) }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[92vh] max-w-3xl flex-col overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
      >
        <DialogTitle className="sr-only">
          {mode === 'create' ? 'Adicionar ícone' : 'Editar ícone'}
        </DialogTitle>
        <PainelPermissionsDialogShell className="min-h-0 flex-1 overflow-hidden">
          <div className="shrink-0 border-b border-white/10 bg-blue-950/30 px-6 py-5 sm:px-8">
            <h2 className="font-poppins text-xl font-bold text-white">
              {mode === 'create' ? 'Adicionar ícone' : 'Editar ícone'}
            </h2>
            <p className="mt-1 font-inter text-sm text-slate-400">
              Cole o código SVG ou faça upload de um ficheiro .svg. O conteúdo é sanitizado no
              servidor.
            </p>
          </div>

          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5 sm:px-8">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-slate-300">Nome</Label>
                <Input
                  value={draft.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className={painelInputClass}
                  placeholder="Power"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Identificador (slug)</Label>
                <Input
                  value={draft.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    patch({ slug: slugifyIconName(e.target.value) });
                  }}
                  className={cn(painelInputClass, 'font-mono text-xs')}
                  placeholder="power"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-slate-300">Categoria</Label>
                {customCategory ? (
                  <Input
                    value={draft.category}
                    onChange={(e) => patch({ category: slugifyIconName(e.target.value) || 'outros' })}
                    className={painelInputClass}
                    placeholder="minha-categoria"
                  />
                ) : (
                  <Select
                    value={draft.category}
                    onValueChange={(v) => {
                      if (v === '__custom__') {
                        setCustomCategory(true);
                        return;
                      }
                      patch({ category: v });
                    }}
                  >
                    <SelectTrigger className={cn(painelInputClass, 'h-10')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={painelSelectContentClass}>
                      {ICON_LIBRARY_CATEGORIES.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.label}
                        </SelectItem>
                      ))}
                      <SelectItem value="__custom__">Outra (personalizada)…</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Renderização</Label>
                <Select
                  value={draft.renderMode}
                  onValueChange={(v) => patch({ renderMode: v as IconRenderMode })}
                >
                  <SelectTrigger className={cn(painelInputClass, 'h-10')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={painelSelectContentClass}>
                    <SelectItem value="mask">Máscara (monocromático)</SelectItem>
                    <SelectItem value="img">Imagem (cores preservadas)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Descrição (opcional)</Label>
              <Input
                value={draft.description}
                onChange={(e) => patch({ description: e.target.value })}
                className={painelInputClass}
                placeholder="Breve descrição do ícone"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Tags (opcional, separadas por vírgula)</Label>
              <Input
                value={draft.tags}
                onChange={(e) => patch({ tags: e.target.value })}
                className={painelInputClass}
                placeholder="nav, power, ui"
              />
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-slate-300">SVG</Label>
                {onUploadFile ? (
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1 font-inter text-xs text-sky-200 hover:border-sky-400/40">
                    <Upload className="h-3.5 w-3.5" />
                    Upload .svg
                    <input
                      type="file"
                      accept=".svg,image/svg+xml"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        void onUploadFile(file, draft);
                        e.target.value = '';
                      }}
                    />
                  </label>
                ) : null}
              </div>
              <textarea
                value={draft.svgContent}
                onChange={(e) => patch({ svgContent: e.target.value })}
                className={cn(painelTextareaClass, 'min-h-[160px] font-mono text-xs')}
                spellCheck={false}
              />
            </div>

            {dangerHints.length > 0 ? (
              <div className="flex gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2.5 text-amber-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                <div className="min-w-0 font-inter text-xs">
                  <p className="font-semibold">Conteúdo potencialmente perigoso detectado</p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4 text-amber-100/75">
                    {dangerHints.map((h) => (
                      <li key={h}>{h}</li>
                    ))}
                  </ul>
                  <p className="mt-1 text-amber-100/60">
                    O servidor remove estes elementos ao guardar.
                  </p>
                </div>
              </div>
            ) : null}

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <p className="font-poppins text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Preview
                </p>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 font-inter text-xs text-slate-400">
                    <span>Tamanho</span>
                    <input
                      type="range"
                      min={32}
                      max={128}
                      value={previewSize}
                      onChange={(e) => setPreviewSize(Number(e.target.value))}
                      className="w-24"
                    />
                    <span className="tabular-nums text-slate-300">{previewSize}px</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={previewLight}
                      onCheckedChange={setPreviewLight}
                      className={painelSwitchClass}
                      id="icon-preview-bg"
                    />
                    <Label htmlFor="icon-preview-bg" className="font-inter text-xs text-slate-400">
                      Fundo claro
                    </Label>
                  </div>
                </div>
              </div>
              <IconSvgPreview
                svgContent={draft.svgContent}
                renderMode={draft.renderMode}
                size={previewSize}
                background={previewLight ? 'light' : 'dark'}
              />
            </div>
          </div>

          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/40 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-8">
            <Button
              type="button"
              variant="ghost"
              className={painelModalCancelButtonClass}
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className={cn(painelButtonPrimaryClass, 'h-10 px-5')}
              disabled={saving || !draft.name.trim() || !draft.svgContent.trim()}
              onClick={() => void onSave(draft)}
            >
              {saving ? 'A guardar…' : mode === 'create' ? 'Criar ícone' : 'Guardar'}
            </Button>
          </div>
        </PainelPermissionsDialogShell>
      </DialogContent>
    </Dialog>
  );
}
